import React, { useEffect, useState, useContext } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Grid } from "@mui/material";
import { useTranslation, withTranslation } from "react-i18next";
import {
  ConnectComponentsProvider,
  ConnectAccountOnboarding,
} from "@stripe/react-connect-js";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import { useStripeOnboarding } from "../../shared/hooks/useStripeOnboarding";
import StripeComplete from "./StripeComplete";
import StripeOnboardingLoadingState from "./StripeOnboardingLoadingState";
import StripeOnboardingErrorState from "./StripeOnboardingErrorState";
import MotionChevronRight from "../../shared/Icons/MotionChevronRight";
import { ModalContext } from "../../shared/components/EMPModal/ModalContext";
import ChecklistPDF from "../ChecklistPDF";
import PDF from "../../shared/content/stripe-job-aid-compressed.pdf";
import {
  trackPageLoadEvent,
  trackOnboardingOnClickEvent,
  trackOnClickEvent,
} from "../../shared/services/analytics/AnalyticsService";
import { useFieldSupportStatus } from "../../shared/hooks/useFieldSupportStatus";

const PersistentButtons = ({ user, t, handleModal }) => (
  <Box sx={{ mb: 4 }}>
    <Grid container alignItems="center" spacing={4}>
      <Grid item>
        <EMPButton
          variant="outlined"
          rightIcon={<MotionChevronRight />}
          onClick={() => {
            // Track analytics click event
            if (user) {
              trackOnClickEvent(
                "fv:emp:nvc:b2c:stripe setup",
                "emp:nvc:b2c:stripe setup:content:stripe account setup job aid",
                "fv:emp:nvc:b2c:stripe setup:content action",
                user,
                "emp:nvc"
              );
            }
            handleModal(
              t("NVCOnboarding.stripe.checklist"),
              <ChecklistPDF file={PDF} />
            );
          }}
        >
          {t("NVCOnboarding.stripe.checklist")}
        </EMPButton>
      </Grid>
    </Grid>
  </Box>
);

PersistentButtons.propTypes = {
  handleModal: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  user: PropTypes.object,
};

/**
 * Stripe onboarding component designed to be embedded within the main onboarding layout
 * This is a wrapper around the original StripeOnboarding content without PageWrapper
 */
const StripeOnboardingEmbedded = ({ user }) => {
  const { t } = useTranslation();
  const [isMounted, setIsMounted] = useState(false);
  const [isExitingStripe, setIsExitingStripe] = useState(false);
  const { handleModal } = useContext(ModalContext);
  const isFieldSupport = useFieldSupportStatus();

  const {
    errorMessage,
    isLoading,
    stripeConnectInstance,
    isAccountEnabled,
    isReady,
    handleCreateAccount,
    handleInitializeStripe,
    detailsSubmitted,
    statusMessage,
    accountId,
    createdTimestamp,
  } = useStripeOnboarding();

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    // Store in sessionStorage to track across the session
    sessionStorage.setItem("stripe.onboardingViewed", "true");
    if (user) {
      trackPageLoadEvent("fv:emp:nvc:b2c:stripe setup", user, "emp:nvc");
    }
  }, [user]);

  // Loading state
  if (!isMounted || isLoading || isExitingStripe) {
    return <StripeOnboardingLoadingState isExitingStripe={isExitingStripe} />;
  }

  // Error state
  if (errorMessage && errorMessage !== "ACCOUNT_NOT_FOUND") {
    return (
      <StripeOnboardingErrorState t={t} onRetry={handleInitializeStripe} />
    );
  }

  // Handle onboarding exit with proper cleanup
  const handleOnboardingExit = (accountObj) => {
    trackOnboardingOnClickEvent(
      "fv:emp:nvc:b2c:stripe setup",
      "emp:nvc:b2c:stripe setup:content:submit",
      "fv:emp:nvc:b2c:stripe setup:content action",
      user,
      "stripe onboarding complete"
    );

    console.log("The account has exited onboarding", accountObj);
    setIsExitingStripe(true);
    setTimeout(() => {
      handleInitializeStripe().finally(() => {
        setIsExitingStripe(false);
      });
    }, 5000);
  };

  // Handle step changes during onboarding
  const handleStepChange = (stepChange) => {
    console.log(`User entered: ${stepChange.step}`);
  };

  // Helper function to render Setup Stripe tab content based on state
  const renderSetupStripeContent = () => {
    if (
      errorMessage === "ACCOUNT_NOT_FOUND" ||
      (!stripeConnectInstance && !isAccountEnabled)
    ) {
      return (
        <Box>
          <Typography variant="body1" sx={{ mb: 10 }}>
            {t("NVCOnboarding.stripe.introDescription")}
          </Typography>
          <EMPButton
            variant="contained"
            onClick={() => {
              trackOnboardingOnClickEvent(
                "fv:emp:nvc:b2c:stripe setup",
                "emp:nvc:b2c:stripe setup:content:begin stripe setup",
                "fv:emp:nvc:b2c:stripe setup:content action",
                user,
                "stripe onboarding start"
              );

              handleCreateAccount();
            }}
            disabled={isLoading || isFieldSupport}
          >
            {t("NVCOnboarding.stripe.beginStripeSetup")}
          </EMPButton>
        </Box>
      );
    }

    // Details submitted - show completion
    if (detailsSubmitted) {
      return (
        <StripeComplete
          statusMessage={statusMessage}
          accountId={accountId}
          stripeConnectInstance={stripeConnectInstance}
          createdTimestamp={createdTimestamp}
          isReady={isReady}
          user={user}
          showEditPreferences={false}
        />
      );
    }

    // Show onboarding form
    return (
      <Box
        sx={{
          border: "1px solid #E0E0E0",
          borderRadius: "8px",
          p: 4,
          mb: 4,
          minHeight: "200px",
        }}
      >
        <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
          <ConnectAccountOnboarding
            onExit={handleOnboardingExit}
            onStepChange={handleStepChange}
          />
        </ConnectComponentsProvider>
      </Box>
    );
  };

  return (
    <>
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" sx={{ mb: 2 }}>
          {t("NVCOnboarding.stripe.title")}
        </Typography>
      </Box>

      <div>
        <PersistentButtons user={user} t={t} handleModal={handleModal} />
        {renderSetupStripeContent()}
      </div>
    </>
  );
};

StripeOnboardingEmbedded.propTypes = {
  user: PropTypes.object,
};

export default withTranslation("emp")(StripeOnboardingEmbedded);
